/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { ChangeEvent, useState } from 'react';

import { showToast } from '@opendoor/bricks/complex/Toast';
import { Box, Button, Text } from '@opendoor/bricks/core';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { OdProtosListingsData_Listing_State } from '../../__generated__/athenaGlobalTypes';
import { CheckIcon } from '../../components/icons/CheckIcon';
import { useSetListingDetails } from '../../graphql/athena/mutations/SetListingDetails';
import { useSetTimestamps } from '../../graphql/athena/mutations/SetTimestamps';
import { SingleLead } from '../../helpers/getAllLeadData';
import {
  CLOSED_STATE_STRING,
  FIRST_CONTACT_ATTEMPTED_STATE_STRING,
  IN_CONTRACT_STATE_STRING,
  LISTED_STATE_STRING,
  LISTING_AGREEMENT_SIGNED_STATE_STRING,
  LISTING_PRESENTATION_COMPLETE_STATE_STRING,
  LISTING_PRESENTATION_SCHEDULED_STATE_STRING,
  NEW_REFERRAL_STATE_STRING,
  PostListingStateNames,
} from './utils';

const FirstContactAttemptedTimestampKey = 'FirstContactAttempted';
const ListingPresentationDateTimestampKey = 'ListingPresentationDate';
const ListingPresentationCompleteTimestampKey = 'ListingPresentationComplete';
const SelectedListingTimestampkey = 'SelectedListing';

interface TimelineItem {
  id: string | OdProtosListingsData_Listing_State;
  state: string;
  date?: string;
}

type TimelineProps = {
  leadDetails: SingleLead;
};

function getTimelineDates(leadDetails: SingleLead): TimelineItem[] {
  const lwcTimestampMap = leadDetails.lwcProductData.lwcProductTimestampsMap;
  const listingTimestampMap = leadDetails.lwcProductData.listingStateEntryTimes;
  return [
    {
      id: FirstContactAttemptedTimestampKey,
      state: FIRST_CONTACT_ATTEMPTED_STATE_STRING,
      date: lwcTimestampMap.get(FirstContactAttemptedTimestampKey),
    },
    {
      id: ListingPresentationDateTimestampKey,
      state: LISTING_PRESENTATION_SCHEDULED_STATE_STRING,
      date: lwcTimestampMap.get(ListingPresentationDateTimestampKey),
    },
    {
      id: ListingPresentationCompleteTimestampKey,
      state: LISTING_PRESENTATION_COMPLETE_STATE_STRING,
      date: lwcTimestampMap.get(ListingPresentationCompleteTimestampKey),
    },
    {
      id: SelectedListingTimestampkey,
      state: LISTING_AGREEMENT_SIGNED_STATE_STRING,
      date: lwcTimestampMap.get(SelectedListingTimestampkey),
    },
    {
      id: OdProtosListingsData_Listing_State.LISTING_ACTIVE,
      state: LISTED_STATE_STRING,
      date: listingTimestampMap.get(OdProtosListingsData_Listing_State.LISTING_ACTIVE),
    },
    {
      id: OdProtosListingsData_Listing_State.IN_CONTRACT,
      state: IN_CONTRACT_STATE_STRING,
      date: listingTimestampMap.get(OdProtosListingsData_Listing_State.IN_CONTRACT),
    },
    {
      id: OdProtosListingsData_Listing_State.SOLD,
      state: CLOSED_STATE_STRING,
      date: listingTimestampMap.get(OdProtosListingsData_Listing_State.SOLD),
    },
  ];
}

const Timeline: React.FC<TimelineProps> = ({ leadDetails }) => {
  const timestampsFromBackend = getTimelineDates(leadDetails);
  const [timelineData, setTimelineData] = useState<TimelineItem[]>(timestampsFromBackend);
  const [showEdit, setShowEdit] = useState(false);

  // Timestamps from the BE may change if there has been a state transition:
  // e.g. moving the state to First Contact Attempted which also sets the
  // FirstContactAttempted timestamp.
  useDeepCompareEffect(() => {
    setTimelineData(timestampsFromBackend);
  }, [timestampsFromBackend]);

  const {
    setLwcProductTimestamp,
    error: setLwcProductTimestampError,
    loading: setLwcProductTimestampLoading,
  } = useSetTimestamps();
  const {
    setListingDetails,
    error: setListingDetailsError,
    loading: setListingDetailsLoading,
  } = useSetListingDetails();

  const showLoading = setLwcProductTimestampLoading || setListingDetailsLoading;
  if (setLwcProductTimestampError || setListingDetailsError) {
    showToast('error', 'Error setting timestamps', 'lwc-dashboard-toast', {
      toastOptions: { autoClose: false },
    });
  }

  const handleDateChange = (state: string, event: ChangeEvent<HTMLInputElement>) => {
    const newData = timelineData.map((item) => {
      if (item.state === state) {
        return { ...item, date: event.target.value || undefined };
      }
      return item;
    });
    setTimelineData(newData);
  };

  const handleSave = () => {
    const changeItems = timelineData.filter((item, index) => {
      const itemFromBackend = timestampsFromBackend[index];
      return item.date !== itemFromBackend.date;
    });

    // Filter out changedItems not in PostListingStateNames
    const changedLwcProductTimestamps = changeItems.filter((item) => {
      return !PostListingStateNames.includes(item.state);
    });

    const lwcProductTimestampOverrides = changedLwcProductTimestamps.map((item) => {
      return {
        key: item.id,
        timestamp: new Date(item.date!).toISOString(),
      };
    });

    if (changedLwcProductTimestamps.length > 0) {
      setLwcProductTimestamp({
        params: {
          lwcProductId: leadDetails.lwcProductData.lwcProductId,
          keyTimestampPairs: lwcProductTimestampOverrides,
        },
      });
    }

    const changedListingTimestamps = changeItems.filter((item) => {
      return PostListingStateNames.includes(item.state);
    });

    const listingTimestampOverrides = changedListingTimestamps.map((item) => {
      return {
        state: item.id as OdProtosListingsData_Listing_State,
        timestamp: new Date(item.date!).toISOString(),
      };
    });

    if (changedListingTimestamps.length > 0) {
      setListingDetails({
        params: {
          id: leadDetails.lwcProductData.listingId,
          timestampOverrides: listingTimestampOverrides,
          updateMask: {
            paths: ['timestamp_overrides'],
          },
        },
      });
    }
  };

  const editableTimelineItems = timelineData.filter(
    (item) => item.date !== undefined && item.date !== '',
  );

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => setShowEdit(!showEdit)}
        disabled={showLoading}
        loading={showLoading}
        analyticsName="lwc_dashboard_edit_dates_button"
        aria-label="edit dates of key events"
      >
        Edit dates of key events
      </Button>
      {showEdit && (
        <>
          <Box display="flex" width="100%" mx={5}>
            <Box mb={5} alignItems="center" flexDirection="column" width="100%">
              <Box mb={3}>
                <Box display="inline" ml={3}>
                  <Text>{NEW_REFERRAL_STATE_STRING}</Text>
                  <CheckIcon />
                </Box>
              </Box>
              {editableTimelineItems.map((item) => (
                <Box mb={3} key={item.id}>
                  <Text>{item.state}</Text>
                  <input
                    type="date"
                    value={item.date || ''}
                    onChange={(event) => handleDateChange(item.state, event)}
                  />
                  {item.date && (
                    <Box display="inline" ml={3}>
                      <CheckIcon />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
          <Button
            aria-label="lwc-timeline-save-button"
            analyticsName="lwc-timeline-save-button"
            onClick={handleSave}
            loading={showLoading}
          >
            Save
          </Button>
        </>
      )}
    </>
  );
};

export default Timeline;
